// Kitchen Assist — load the glossary index once and resolve entries on
// demand for the cooking screen.
//
// On mount it fetches the available-words index through the `/api/assist`
// proxy and builds a matcher. Any failure (offline, upstream down) leaves
// the matcher null, so the overlay is simply dormant and task text renders
// verbatim — no errors, no links. Entry bodies are fetched lazily on first
// click and memoised, so re-opening the same word never re-fetches.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AssistEntry } from '@cookchow/shared';
import { fetchAssistEntry, fetchAssistIndex } from '../api/client';
import { buildAssistMatcher, type AssistMatcher } from './assist';

export interface UseAssist {
  /** Matcher over the available help words, or null until/unless loaded. */
  matcher: AssistMatcher | null;
  /** Resolve a canonical word to its entry, or null on any failure. */
  resolveEntry: (word: string) => Promise<AssistEntry | null>;
}

export function useAssist(): UseAssist {
  const [words, setWords] = useState<string[] | null>(null);
  const cacheRef = useRef<Map<string, AssistEntry>>(new Map());

  useEffect(() => {
    let alive = true;
    fetchAssistIndex()
      .then((w) => {
        if (alive) setWords(w);
      })
      .catch(() => {
        // Dormant overlay — leave words null.
      });
    return () => {
      alive = false;
    };
  }, []);

  const matcher = useMemo(
    () => (words && words.length > 0 ? buildAssistMatcher(words) : null),
    [words],
  );

  const resolveEntry = useCallback(async (word: string): Promise<AssistEntry | null> => {
    const cache = cacheRef.current;
    const hit = cache.get(word);
    if (hit) return hit;
    try {
      const entry = await fetchAssistEntry(word);
      cache.set(word, entry);
      return entry;
    } catch {
      return null;
    }
  }, []);

  return { matcher, resolveEntry };
}
